/* eslint-disable max-lines */
/**
 * @file Formatting and unit conversion helpers
 */

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import humanizeDuration from 'humanize-duration'

import { RestModels } from '@t3/rest-proptypes'
import { roundNumber, HtmlEntities } from '@wf-sl/common-utilities'

import logger from '../common/log'
import ADDRESS_FORMATS from './address-formats'
import DATE_FORMATS from './date-formats'
import NUMBER_FORMATS from './number-formats'
import {
    DistUnit,
    EnergyEfficiencyUnit,
    EnergyUnit,
    FuelEfficiencyUnit,
    GaseousFuelEfficiencyUnit,
    LiquidUnit,
    PressureUnit,
    RpmUnit,
    SpeedUnit,
    TemperatureUnit,
    WeightUnit,
    PowerUnit
} from './units'

dayjs.extend(duration)

const MDASH = HtmlEntities.MDASH
const NBSP = HtmlEntities.NBSP

// Regex pattern for replaceable variables defined in translation template, such as 'This is a {$variable}. '
const TRANSLATION_PARAM_REGEX = /(?:\{\$([^}]+)\})|(?:\$(\w+))/

// Regex pattern for input time date string, in the format of '22.02.2012 22:45:00'
const DATE_REGEX = /(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})/

// Regex pattern for an ISO8601 duration borrowed from moment
// See also https://github.com/moment/moment/blob/develop/src/lib/duration/create.js
const ISO_DURATION_REGEX = new RegExp(
    '^(-)?P(?:(-?[0-9,.]*)Y)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)W)?(?:(-?[0-9,.]*)D)?' +
        '(?:T(?:(-?[0-9,.]*)H)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)S)?)?$'
)

const DAY_STRINGS = ['wf_monday', 'wf_tuesday', 'wf_wednesday', 'wf_thursday', 'wf_friday', 'wf_saturday', 'wf_sunday']

const DAY_STRINGS_SHORT = [
    'wf_monday_short',
    'wf_tuesday_short',
    'wf_wednesday_short',
    'wf_thursday_short',
    'wf_friday_short',
    'wf_saturday_short',
    'wf_sunday_short'
]

const defaultNumberSeparators = {
    decimal: undefined,
    thousands: undefined
}

const numberFormatPattern = /\d(\D)?\d{3}(\D)?\d{2}/

/**
 * Creates an Intl object according to the provided locale and IntlConfig options.
 * The Intl object provides a convenient interface for formatting and translation purpose.
 *
 * @constructs Intl
 * @param {string} locale - The desired locale string e.g. de-DE, en-US
 * @param {IntlConfig} - The Intl config object
 * @returns {Intl} The generated Intl object
 */
export function createIntl(
    locale,
    {
        coordinateFormat = 'DMS',
        dateFormatOptions = DATE_FORMATS,
        distUnit = 'km',
        energyEfficiencyUnit = 'kWh/100km',
        fuelEfficiencyUnit = 'l/100km',
        numberFormatOptions = NUMBER_FORMATS,
        pressureUnit = 'bar',
        temperatureUnit = 'Celsius',
        translations = {}
    }
) {
    if (!locale) {
        logger.warn('Locale not set. Likely fetching the translations failed. Using default locale "en-GB".')
        locale = 'en-GB'
    }

    // Determine actual target unit specifications, to which the formatters below will convert the source unit
    const {
        currentEnergyEfficiencyUnit,
        currentFuelEfficiencyUnit,
        currentPressureUnit,
        currentTemperatureUnit,
        distUnitBig,
        distUnitSmall,
        energyUnit,
        gaseousFuelEfficiencyUnit,
        liquidUnit,
        powerUnit,
        rpmUnit,
        speedUnit,
        weightUnit
    } = createUnits()

    const numberSeparators = getFormattedNumberSeparators()

    // The language taken from the locale
    const language = locale.split('-')[0]
    // Create and cache date formatters
    const dateFormatters = createDateFormatters()
    // Create and cache number formatters
    const numberFormatters = createNumberFormatters()
    // Create helper object for parameter replacement in translations
    const stringifiers = createStringifiers()
    // humanizer for duration
    const lang = `short_${language}`
    const durationHumanizer = humanizeDuration.humanizer({
        conjunction: ' ',
        spacer: ' ',
        delimiter: ' ',
        round: true,
        decimal: numberSeparators.decimal,
        serialComma: false,
        language: lang,
        languages: {
            [lang]: {
                y: 'y',
                mo: 'mo',
                w: 'w',
                d: 'd',
                h: 'h',
                m: 'min',
                s: 's'
            }
        }
    })

    /**
     * Formats a number according to predefined format configurations as defined in numberFormatters
     *
     * @memberof! Intl#
     * @param {number} number - the number to be formatted
     * @param {string} formatKey - the format configuration to be used
     * @returns {string} the formatted number
     */
    function formatNumber(number, formatKey = 'default') {
        const numberFormat = numberFormatters[formatKey] || numberFormatters['default']
        // According to https://github.com/rxaviers/ecma402-number-format-round-option Intl.Numberformat
        // always rounds to 'half-even'. So we need to implement rounding ourselves.
        // Use numberFormatOptions.maximumFractionDigits as rounding precision.
        const options = numberFormatOptions[formatKey] || numberFormatOptions['default']
        // Default to precision of 2 if maximumFractionDigits is not defined in numberFormatOptions
        const { maximumFractionDigits: precision = 2 } = options

        if (isNaN(number)) return MDASH

        return numberFormat.format(roundNumber(number, precision))
    }

    /**
     * Formats a currency value according to specified options.
     * In case of forcing currency position, it maintains the separator between currency and value according to the locale.
     *
     * @param {Object} params - The parameters for formatting.
     * @param {number} params.value - The numeric value to be formatted.
     * @param {string} params.currency - The ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP', etc.).
     * @param {boolean} [params.useCode=false] - Set to true to use the currency code instead of the symbol.
     * @param {number} [params.decimals] - The number of decimal places to display.
     * @param {string|null} [params.forcePosition] - Can be 'before' or 'after' to force the position of the symbol/code.
     * @returns {string} The formatted currency string.
     *
     * @example
     * // Returns "$123,456.79"
     * formatCurrency({ value: 123456.789, currency: 'USD', useCode: false, decimals: 2, forcePosition: 'before' })
     *
     * @example
     * // Returns "123,457 EUR"
     * formatCurrency({ value: 123456.789, currency: 'EUR', useCode: true, decimals: 0, forcePosition: 'after' })
     */
    function formatCurrency({ currency, decimals, forcePosition, useCode = false, value }) {
        const options = {
            currency: currency,
            style: 'currency',
            ...(decimals
                ? {
                      minimumFractionDigits: decimals,
                      maximumFractionDigits: decimals
                  }
                : {}),
            ...(useCode ? { currencyDisplay: 'code' } : { currencyDisplay: 'symbol' })
        }

        const formatter = new Intl.NumberFormat(locale, options)
        let formattedValue = formatter.format(value)

        if (['before', 'after'].includes(forcePosition)) {
            const currencyIdentifier = useCode
                ? currency
                : formatter.formatToParts(value).find(part => part.type === 'currency').value
            const separator = formattedValue.includes(NBSP) ? NBSP : ''
            const valueWithoutCurrency = formattedValue.replace(currencyIdentifier, '').trim()

            formattedValue =
                forcePosition === 'before'
                    ? `${currencyIdentifier}${separator}${valueWithoutCurrency}`
                    : `${valueWithoutCurrency}${separator}${currencyIdentifier}`
        }

        return formattedValue
    }

    /**
     * Formats a date according to predefined format configurations as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date} date - the date to be formatted
     * @param {string} formatKey - the format configuration to be used
     * @returns {string} the formatted date
     */
    function formatDate(date, formatKey = 'defaultDateTime') {
        const dateFormatter = dateFormatters[formatKey] || dateFormatters['defaultDatetime']
        return (date !== undefined && date != null && dateFormatter.format(date).split('\u200e').join('').trim()) || ''
    }

    /**
     * Formats a time using the 'defaultTime' format configuration as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date|number} date - the date/milliseconds to be formatted
     * @returns {string} the formatted time
     */
    function formatTimeOnly(date) {
        return formatDate(date, 'defaultTime')
    }

    /**
     * Formats a time using the 'defaultFullTime' format configuration as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date|number} date - the date/milliseconds to be formatted
     * @returns {string} the formatted time
     */
    function formatFullTimeOnly(date) {
        return formatDate(date, 'defaultFullTime')
    }

    /**
     * Formats a time using the 'UTCTime' format configuration as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date|number} date - the date/milliseconds to be formatted
     * @returns {string} the formatted time
     */
    function formatTimeOnlyUTC(date) {
        return formatDate(date, 'UTCTime')
    }

    /**
     * Formats a time using the 'UTCFullTime' format configuration as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date|number} date - the date/milliseconds to be formatted
     * @returns {string} the formatted time
     */
    function formatFullTimeOnlyUTC(date) {
        return formatDate(date, 'UTCFullTime')
    }

    /**
     * Formats a date using the 'defaultDate' format configuration as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date} date - the date to be formatted
     * @returns {string} the formatted date
     */
    function formatDateOnly(date) {
        return formatDate(date, 'defaultDate')
    }

    /**
     * Formats a date as UTC using the 'UTCDate' format configuration as defined in dateFormatters
     *
     * @memberof! Intl#
     * @param {Date} date - the date to be formatted
     * @returns {string} the formatted date
     */
    function formatDateOnlyUTC(date) {
        return formatDate(date, 'UTCDate')
    }

    /**
     * Formats a distance given in meters by converting the value to the current target unit (e.g. miles)
     *
     * @memberof! Intl#
     * @param {number} m - the value to be formatted in meters
     * @param {number} [threshold=5000]
     * @param {string} [formatKeySmallUnit='smallUnit']
     * @param {string} [formatKeyBigUnit='bigUnit']
     * @returns {string} the formatted distance
     */
    function formatMeters(m, threshold = 5000, formatKeySmallUnit = 'smallUnit', formatKeyBigUnit = 'bigUnit') {
        const formattedMetersObject = formatMetersObject(m, threshold, formatKeySmallUnit, formatKeyBigUnit)
        return `${formattedMetersObject.value} ${formattedMetersObject.unit}`
    }

    /**
     * @typedef formattedValue
     * @type {Object}
     * @property {string} formatted value
     * @property {string} formatted unit
     */

    /**
     * Formats a distance given in meters by converting the value to the current target unit (e.g. miles)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} m - the value to be formatted in meters
     * @param {number} [threshold=5000]
     * @param {string} [formatKeySmallUnit='smallUnit']
     * @param {string} [formatKeyBigUnit='bigUnit']
     * @returns {formattedValue} the formatted distance object
     */
    function formatMetersObject(m, threshold = 5000, formatKeySmallUnit = 'smallUnit', formatKeyBigUnit = 'bigUnit') {
        const mConverted = convertDistUnitTo(m, m > threshold ? distUnitBig : distUnitSmall)
        if (m > threshold) {
            // Convert to big target dist unit (usually km or mi)
            return {
                value: formatNumber(mConverted, formatKeyBigUnit),
                unit: translate(distUnitBig.translation)
            }
        }
        // Convert to small target dist unit (usually m or yd)
        return {
            value: formatNumber(mConverted, formatKeySmallUnit),
            unit: translate(distUnitSmall.translation)
        }
    }

    /**
     * Formats a speed value given in km/h by converting the value to the current target unit (e.g. mph)
     *
     * @memberof! Intl#
     * @param {number} kmh
     * @param {string} [formatKey='integer']
     * @returns {string} the formatted speed value
     */
    function formatKmh(kmh, formatKey = 'integer') {
        const formattedKmhObject = formatKmhObject(kmh, formatKey)
        return `${formattedKmhObject.value} ${formattedKmhObject.unit}`
    }

    /**
     * Formats a speed value given in km/h by converting the value to the current target unit (e.g. mph)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} kmh
     * @param {string} [formatKey='integer']
     * @returns {formattedValue} the formatted speed object
     */
    function formatKmhObject(kmh, formatKey = 'integer') {
        return {
            value: formatNumber(convertSpeedUnitTo(kmh), formatKey),
            unit: translate(speedUnit.translation)
        }
    }

    /**
     * Formats a weight value given in kg by converting the value to the current target unit (e.g. lbs)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} kg
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted weight object
     */
    function formatKgObject(kg, formatKey = 'bigUnit') {
        return {
            value: formatNumber(convertWeightUnitTo(kg), formatKey),
            unit: translate(weightUnit.translation)
        }
    }

    /**
     * Formats a weight value given in kg by converting the value to the current target unit (e.g. lbs)
     *
     * @memberof! Intl#
     * @param {number} kg
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted weight value
     */
    function formatKg(kg, formatKey = 'bigUnit') {
        const formattedKgObject = formatKgObject(kg, formatKey)
        return `${formattedKgObject.value} ${formattedKgObject.unit}`
    }

    /**
     * Formats a weight value given in grams by converting the value to the current target unit (e.g. kg)
     *
     * @memberof! Intl#
     * @param {number} grams
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted weight value
     */
    function formatGrams(grams, formatKey = 'bigUnit') {
        const formattedGramsObject = formatGramsObject(grams, formatKey)
        return `${formattedGramsObject.value} ${formattedGramsObject.unit}`
    }

    /**
     * Formats a weight value given in grams by converting the value to the current target unit (e.g. kg)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} grams
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted weight object
     */
    function formatGramsObject(grams, formatKey = 'bigUnit') {
        return {
            value: formatNumber(convertWeightUnitTo(grams / 1000), formatKey),
            unit: translate(weightUnit.translation)
        }
    }

    /**
     * Formats a liquid value given in liters by converting the value to the current target unit (e.g. gallons)
     *
     * @memberof! Intl#
     * @param {number} l
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted liquid value
     */
    function formatLiters(l, formatKey = 'bigUnit') {
        const formattedLitersObject = formatLitersObject(l, formatKey)
        return `${formattedLitersObject.value} ${formattedLitersObject.unit}`
    }

    /**
     * Formats a liquid value given in liters by converting the value to the current target unit (e.g. gallons)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} l
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted liquid object
     */
    function formatLitersObject(l, formatKey = 'bigUnit') {
        return {
            value: formatNumber(convertLiquidUnitTo(l), formatKey),
            unit: translate(liquidUnit.translation)
        }
    }

    /**
     * Formats a liquid value given in milliliters by converting the value to the current target unit (e.g. gallons)
     *
     * @memberof! Intl#
     * @param {number} ml
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted liquid value
     */
    function formatMilliliters(ml, formatKey = 'bigUnit') {
        const formattedMillilitersObject = formatMillilitersObject(ml, formatKey)
        return `${formattedMillilitersObject.value} ${formattedMillilitersObject.unit}`
    }

    /**
     * Formats a liquid value given in milliliters by converting the value to the current target unit (e.g. gallons)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} ml
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted liquid object
     */
    function formatMillilitersObject(ml, formatKey = 'bigUnit') {
        return {
            value: formatNumber(convertLiquidUnitTo(ml / 1000), formatKey),
            unit: translate(liquidUnit.translation)
        }
    }

    /**
     * Formats a fuel efficiency value given in ml/m by converting the value to the current target unit (e.g. l/100 km)
     *
     * @memberof! Intl#
     * @param {number} mlPerM
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted fuel efficiency value
     */
    function formatFuelEfficiency(mlPerM, formatKey = 'bigUnit') {
        const formattedFuelEfficiencyObject = formatFuelEfficiencyObject(mlPerM, formatKey)
        return `${formattedFuelEfficiencyObject.value} ${formattedFuelEfficiencyObject.unit}`
    }

    /**
     * Formats a fuel efficiency value given in ml/m by converting the value to the current target unit (e.g. l/100 km)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} mlPerM
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted fuel efficiency object
     */
    function formatFuelEfficiencyObject(mlPerM, formatKey = 'bigUnit') {
        const convertedFuelEfficiency = convertFuelEfficiencyUnitTo(mlPerM)
        return {
            value: formatNumber(convertedFuelEfficiency, formatKey),
            unit: translate(currentFuelEfficiencyUnit.translation)
        }
    }

    /**
     * Formats a gaseous fuel efficiency value given in grams/meter
     * by converting the value to the current target unit: kg/100km
     *
     * @memberof! Intl#
     * @param {number} gPerM gaseous fuel efficiency passed in grams per meter
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted fuel efficiency value in kg/100km
     */
    function formatGaseousFuelEfficiency(gPerM, formatKey = 'bigUnit') {
        const formattedGaseousFuelEfficiencyObject = formatGaseousFuelEfficiencyObject(gPerM, formatKey)
        return `${formattedGaseousFuelEfficiencyObject.value} ${formattedGaseousFuelEfficiencyObject.unit}`
    }

    /**
     * Formats a gaseous fuel efficiency value given in grams/meter
     * by converting the value to the current target unit: kg/100km
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} gPerM gaseous fuel efficiency passed in grams per meter
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted fuel efficiency object
     */
    function formatGaseousFuelEfficiencyObject(gPerM, formatKey = 'bigUnit') {
        const convertedGaseousFuelEfficiency = convertGaseousFuelEfficiencyUnitTo(
            gPerM,
            GaseousFuelEfficiencyUnit.kgPer100km
        )
        return {
            value: formatNumber(convertedGaseousFuelEfficiency, formatKey),
            unit: translate(gaseousFuelEfficiencyUnit.translation)
        }
    }

    /**
     * Formats an energy efficiency value given in kWh/100km by converting the value to the current target unit (e.g. kWh/100mi)
     *
     * @memberof! Intl#
     * @param {number} kWhPer100km
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted energy efficiency value
     */
    function formatEnergyEfficiency(kWhPer100km, formatKey = 'bigUnit') {
        const formattedEnergyEfficiencyObject = formatEnergyEfficiencyObject(kWhPer100km, formatKey)
        return `${formattedEnergyEfficiencyObject.value} ${formattedEnergyEfficiencyObject.unit}`
    }

    /**
     * Formats an energy efficiency value given in kWh/100km by converting the value to the current target unit (e.g. kWh/100mi)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} kWhPer100km
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted energy efficiency object
     */
    function formatEnergyEfficiencyObject(kWhPer100km, formatKey = 'bigUnit') {
        const convertedEnergyEfficiency = convertEnergyEfficiencyUnitTo(kWhPer100km)
        return {
            value: formatNumber(convertedEnergyEfficiency, formatKey),
            unit: translate(currentEnergyEfficiencyUnit.translation)
        }
    }

    /**
     * Formats an energy unit value given in kWh by converting the value to the current target unit (e.g. wh)
     *
     * @memberof! Intl#
     * @param {number} kWh
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted energy unit value
     */
    function formatEnergyUnit(kWh, formatKey = 'bigUnit') {
        const formattedEnergyUnitObject = formatEnergyUnitObject(kWh, formatKey)
        return `${formattedEnergyUnitObject.value} ${formattedEnergyUnitObject.unit}`
    }

    /**
     * Formats an energy unit value given in kWh by converting the value to the current target unit (e.g. wh)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} kWh
     * @param {string} [formatKey='bigUnit']
     * @returns {formattedValue} the formatted energy efficiency object
     */
    function formatEnergyUnitObject(kWh, formatKey = 'bigUnit') {
        const energy = convertEnergyUnitTo(kWh)
        return {
            value: formatNumber(energy, formatKey),
            unit: translate(energyUnit.translation)
        }
    }

    /**
     * Formats a rpm value
     *
     * @memberof! Intl#
     * @param {number} rpm
     * @param {string} [formatKey='integer']
     * @returns {string} the formatted rpm value
     */
    function formatRpm(rpm, formatKey = 'integer') {
        return `${formatNumber(rpm, formatKey)} ${translate(rpmUnit.translation)}`
    }

    /**
     * Formats a percent value
     *
     * @memberof! Intl#
     * @param {number} percent
     * @param {string} [formatKey='bigUnit']
     * @returns {string} the formatted percent value
     */
    function formatPercent(percent, formatKey = 'bigUnit') {
        return `${formatNumber(percent, formatKey)} %`
    }

    /**
     * Formats a given ISO-8601 duration value in a human readable format. The given duration string is validated
     * against a RegEx to be ISO8601 compliant.
     * Calls {@link formatDuration} internally.
     *
     * @memberof! Intl#
     * @param {string} durationAsIso8601    - The duration value as ISO-8601 formatted string, e.g. 'PT1S' for 1s.
     * @param {string[]} [units]            - See {@link formatDuration} for default value.
     * @param {string} [defaultValue]       - See {@link formatDuration} for default value.
     * @returns {string} - a human readable duration.
     */
    function formatIsoDuration(durationAsIso8601, units, defaultValue) {
        const validDuration = String(durationAsIso8601).search(ISO_DURATION_REGEX) > -1
        return formatDurationMs(
            validDuration ? dayjs.duration(durationAsIso8601).asMilliseconds() : NaN,
            units,
            defaultValue
        )
    }

    /**
     * Formats a given duration value in seconds in a human readable format.
     * The value of the smallest time unit will be rounded.
     *
     * @memberof! Intl#
     * @param {number} durationInSeconds            - The duration value in seconds.
     * @param {string[]} [units=['d','h','m','s']]  - An array of time units relevant for the formatting process.
     * @param {string} [defaultValue='']            - The value returned if the given duration can't be parsed.
     * @returns {string} - a human readable duration.
     */
    function formatDuration(durationInSeconds, units = ['d', 'h', 'm', 's'], defaultValue = '') {
        const durationMs = roundNumber(parseFloat(durationInSeconds) * 1000)
        return formatDurationMs(durationMs, units, defaultValue)
    }

    /**
     * Formats a given duration value in milliseconds in a human readable format.
     * The value of the smallest time unit will be rounded.
     *
     * @memberof! Intl#
     * @param {number} durationInMilliSeconds            - The duration value in seconds.
     * @param {string[]} [units=['d','h','m','s']]  - An array of time units relevant for the formatting process.
     * @param {string} [defaultValue='']            - The value returned if the given duration can't be parsed.
     * @returns {string} - a human readable duration.
     */
    function formatDurationMs(durationInMilliSeconds, units = ['d', 'h', 'm', 's'], defaultValue = '') {
        const duration = parseInt(durationInMilliSeconds, 10)
        if (!isNaN(duration)) {
            const sign = duration < 0 ? '-' : ''
            const humanizedDuration = durationHumanizer(duration, { units })
            return `${sign}${humanizedDuration}`
        }
        return defaultValue
    }

    /**
     * Convenience functions for formatting webfleet messages according to a numeric message type
     *
     * @memberof! Intl#
     * @param {number} msgType - the message type
     * @param {Object} params - map of replacement parameter names to their actual value
     * @returns {string} the final formatted message
     */
    function formatMessage(msgType, params) {
        return translate('wf_msgtype_' + msgType, params).replace(/[\r\n]+/g, ' ')
    }

    /**
     * Formats a position according to a reverse geocoded location from our GeoServer
     *
     * @memberof! Intl#
     * @param {number} descType - A number, which denotes a reverse gecoding result type
     * @param {string} suffix - The suffix for the string code (either '' or '_short')
     * @param {Object} params - Position description params as return in the reverse geocoding result
     * @returns {string} a human readable position description
     */
    function formatPosition(descType, suffix, params) {
        const stringCode = `wf_locationtype_${descType}${suffix}`
        let result = translate(stringCode, params)
        // format translated position string, remove empty brackets, multiple colons and white space.
        result = result.replace(/\s{2,}/gm, ' ') // replace multiple spaces with just one space
        result = result.replace(/\(\s*([^)]*)\s*\)/g, '($1)') // remove leading and ending spaces between brackets
        result = result.replace(/\(\s*\)/g, '') // remove empty brackets
        result = result.replace(/,\s*,/g, ',') // replace multiple colons with just one colon
        result = result.replace(/^,/g, '') // remove beginning colon
        result = result.replace(/,\s*$/gm, '') // remove ending colon
        result.trim()
        return result
    }

    /**
     * Formats a pair of latitude, longitude
     *
     * @memberof! Intl#
     * @param {number} latitude
     * @param {number} longitude
     * @param {string} format The format for the coordinates ('DMS' or 'DD')
     * @returns {string} the formatted coordinates
     */
    function formatCoordinates(latitude, longitude) {
        if (coordinateFormat === RestModels.CoordinateFormats.DMS) {
            const latDms = convert2Dms(latitude)
            const lonDms = convert2Dms(longitude)
            const latDir = latitude < 0 ? 'S' : 'N'
            const lonDir = longitude < 0 ? 'W' : 'E'
            const latFormatted = formatDmsCoord(latDms, latDir)
            const lonFormatted = formatDmsCoord(lonDms, lonDir)
            return latFormatted + ', ' + lonFormatted
        } else if (coordinateFormat === RestModels.CoordinateFormats.DD) {
            return latitude.toFixed(6) + ', ' + longitude.toFixed(6)
        } else {
            throw new Error('Invalid format provided: ' + coordinateFormat)
        }
    }

    /** Get the formatted address text via address object.
     *
     * @memberof! Intl#
     * @param {Object} address - address data object for formatting address text.
     * @param {string} address.name1 - The name of address object.
     * @param {string} address.countryCode - The countryCode of address object.
     * @param {string} address.zip - The postcode of address object.
     * @param {string} address.city - The city of address object.
     * @param {string} address.street - The street name of address object.
     * @param {string} address.number - The street number of address object.
     * @param {string} address.federalState - The federal state of address object.
     * @param {boolean} includeName - if formatted address includes address name, default value true.
     * @see ADDRESS_FORMATS
     * @returns {string} a human readable address description
     */
    function formatAddress(address, includeName = true) {
        // Get address format according to address locale, if address locale is not present
        // get it according to intl locale.
        const addressFormat =
            ADDRESS_FORMATS[address.countryCode] || // if countryCode is present and format is found
            ADDRESS_FORMATS[locale.split('-')[1]] || // if code is not present or format is not found
            ADDRESS_FORMATS['GB'] // fallback if country code from locale is also not included in ADDRESS_FORMATS
        const addressCopy = Object.assign({}, address)

        // if exclude address name flag is set, delete name variable in the copied address object.
        if (!includeName) {
            delete addressCopy['name1']
        }

        let formattedAddress = replaceStringParameters(addressFormat, addressCopy)

        // format translated position string, remove empty brackets, white space and tab.
        formattedAddress = formattedAddress.replace(/(?:\s*,\s*)+/g, ', ')
        formattedAddress = formattedAddress.replace(/\s{2,}/g, ' ')
        formattedAddress = formattedAddress.replace(/^[\s,]+/, '')
        formattedAddress = formattedAddress.replace(/[\s,]+$/, '')

        return formattedAddress
    }

    /**
     * Returns the translation for a given string code with optional replacement of template parameters within the
     * translated result string. Example: `It lasts {$var,duration}`
     *
     * @memberof! Intl#
     * @param {string} stringCode             - The string code to retrieve the translation for.
     * @param {Object} [params]               - An optional parameter object whose keys are referenced as parameter
     *                                          names in the translated string.
     * @param {string|Function} [placeholder] - An optional placeholder to be returned if no translation can be found.
     * @returns {string} the translation for the given string code
     */
    function translate(stringCode, params, placeholder = stringCode => `###${stringCode}###`) {
        // Translation are stored in a locale -> stringCode -> translation hierarchy, so first retrieve the translation
        // sub-tree for the desired locale
        const dictionary = translations[locale]
        let translation
        if (dictionary) {
            // localeTranslations consist of stringCode -> translation pairs
            translation = dictionary[stringCode]
        }
        // Return placeholder if no translation can be found after all
        if (!translation) {
            return typeof placeholder === 'function' ? placeholder(stringCode) : placeholder
        }

        return replaceStringParameters(translation, params)
    }

    /**
     * Fills actual values into a translation template
     *
     * @memberof! Intl#
     * @param {string} template - the template string
     * @param {Object} params - map of parameter names to actual values
     * @returns {string} the final translation string
     */
    function replaceStringParameters(template, params) {
        let result = ''
        let match
        let src = template

        // Iterate over and collect all of the matches for variable patterns in the translation template.
        while ((match = TRANSLATION_PARAM_REGEX.exec(src)) != null) {
            const templateParams = match[1] || match[2]
            const [paramName, paramType, paramFormat] = templateParams.split(',')
            let value

            if (params) {
                if (paramType) {
                    const fnReplacement = stringifiers[paramType]

                    if (fnReplacement) {
                        value = fnReplacement(paramName, paramFormat, params)
                    }
                } else {
                    value = params[paramName]
                }
            }

            result += src.slice(0, match.index)

            if (value != null) {
                result += ('' + value).trim()
            }

            src = src.slice(match.index + match[0].length)
        }

        result += src

        return result.replace(/\s{2,}/g, ' ').trim()
    }

    /**
     * Get DistUnit object.
     *
     * @memberof! Intl#
     * @param {object|string} [unit]
     * @returns {Object}
     */
    function getDistUnitObject(unit) {
        if (unit && unit.factor && unit.convert) {
            return unit
        }

        if (unit === 'smallUnit') {
            return distUnitSmall
        }

        return distUnitBig
    }

    /**
     * Get currently chosen gaseous fuel efficiency object.
     *
     * @memberof! Intl#
     * @returns {Object}
     */
    function getGaseousFuelEfficiencyUnitObject() {
        return gaseousFuelEfficiencyUnit
    }

    /**
     * Get currently chosen FuelEfficiency object.
     *
     * @memberof! Intl#
     * @returns {Object}
     */
    function getFuelEfficiencyUnitObject() {
        return currentFuelEfficiencyUnit
    }

    /**
     * Get currently chosen EnergyEfficiency object.
     *
     * @memberof! Intl#
     * @returns {Object}
     */
    function getEnergyEfficiencyUnitObject() {
        return currentEnergyEfficiencyUnit
    }

    /**
     * Get currently chosen EnergyUnit object.
     *
     * @memberof! Intl#
     * @returns {Object}
     */
    function getEnergyUnitObject() {
        return energyUnit
    }

    /**
     * Convert from source distUnit (meter) to given intl target or custom unit.
     *
     * @example
     * intlDE.convertDistUnitTo(1000) == 1 // m -> km
     * intlDE.convertDistUnitTo(1000, DistUnit.mi) == 0.62 // m -> mi
     * intlUS.convertDistUnitTo(1000, 'smallUnit') == 1093.61 // m -> yd
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {object|string} [unit=distUnitBig]
     * @returns {number}
     */
    function convertDistUnitTo(value, unit = distUnitBig) {
        return DistUnit.m.convert(getDistUnitObject(unit), value)
    }

    /**
     * Convert to source distUnit (meter) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertDistUnitFrom(1) == 1000 // km -> m
     * intlDE.convertDistUnitFrom(1000, DistUnit.mi) == 1609344 // mi -> m
     * intlUS.convertDistUnitFrom(1000, 'smallUnit') == 914.4 // yd -> m
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {object|string} [unit=distUnitBig]
     * @returns {number}
     */
    function convertDistUnitFrom(value, unit = distUnitBig) {
        return getDistUnitObject(unit).convert(DistUnit.m, value)
    }

    /**
     * Convert from source speedUnit (kmh) to given intl target or custom unit.
     *
     * @example
     * intlDE.convertSpeedUnitTo(100, SpeedUnit.mph) == 62.1371 // kmh -> mph
     * intlUS.convertSpeedUnitTo(100) == 62.137 // kmh -> mph
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=speedUnit]
     * @returns {number}
     */
    function convertSpeedUnitTo(value, unit = speedUnit) {
        return SpeedUnit.kmh.convert(unit, value)
    }

    /**
     * Convert to source speedUnit (kmh) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertSpeedUnitFrom(100, SpeedUnit.mph) == 160.934 // mph -> kmh
     * intlUS.convertSpeedUnitFrom(100) == 160.934 // mph -> kmh
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=speedUnit]
     * @returns {number}
     */
    function convertSpeedUnitFrom(value, unit = speedUnit) {
        return unit.convert(SpeedUnit.kmh, value)
    }

    /**
     * Convert from source weightUnit (kg) to given intl target or custom unit.
     *
     * @example
     * intlDE.convertWeightUnitTo(1000, WeightUnit.lbs) == 2204.62 // kg -> lbs
     * intlUS.convertWeightUnitTo(1000) == 2204.62 // kg -> lbs
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=weightUnit]
     * @returns {number}
     */
    function convertWeightUnitTo(value, unit = weightUnit) {
        return WeightUnit.kg.convert(unit, value)
    }

    /**
     * Convert to source weightUnit (kg) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertWeightUnitFrom(1000, WeightUnit.lbs) == 453.592 // lbs -> kg
     * intlUS.convertWeightUnitFrom(1000) == 453.592 // lbs -> kg
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=weightUnit]
     * @returns {number}
     */
    function convertWeightUnitFrom(value, unit = weightUnit) {
        return unit.convert(WeightUnit.kg, value)
    }

    /**
     * Convert from source liquidUnit (l) to given intl target or custom unit.
     *
     * @example
     * intlDE.convertLiquidUnitTo(1000, LiquidUnit.galUs) == 264.1720 // l -> galUs
     * intlUS.convertLiquidUnitTo(1000) == 264.1720 // l -> galUs
     * intlUS.convertLiquidUnitTo(1000, LiquidUnit.galImp) == 219.969 // l -> galImp
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=liquidUnit]
     * @returns {number}
     */
    function convertLiquidUnitTo(value, unit = liquidUnit) {
        return LiquidUnit.l.convert(unit, value)
    }

    /**
     * Convert to source liquidUnit (l) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertLiquidUnitFrom(1000, LiquidUnit.galImp) == 4546.09 // galImp -> l
     * intlUS.convertLiquidUnitFrom(1000) == 3785.41 // galUs -> l
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=liquidUnit]
     * @returns {number}
     */
    function convertLiquidUnitFrom(value, unit = liquidUnit) {
        return unit.convert(LiquidUnit.l, value)
    }

    /**
     * Convert from source currentFuelEfficiencyUnit (mlPerM) to given intl target or custom unit.
     *
     * @example
     * intlDE.convertFuelEfficiencyUnitTo(1000) == 100000 // mlPerM -> lPer100km
     * intlDe.convertFuelEfficiencyUnitTo(1000, FuelEfficiencyUnit.mpgUk) == 0.00282 // mlPerM -> mpgUk
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=currentFuelEfficiencyUnit]
     * @returns {number}
     */
    function convertFuelEfficiencyUnitTo(value, unit = currentFuelEfficiencyUnit) {
        return value !== 0 ? FuelEfficiencyUnit.mlPerM.convert(unit, value) : 0
    }

    /**
     * Convert to source
     *
     * @example
     * intlDE.convertGaseousFuelEfficiencyUnitFrom(1000, GaseousFuelEfficiencyUnit.kgPer100km) == 100000
     * intlUS.convertGaseousFuelEfficiencyUnitFrom(1000, GaseousFuelEfficiencyUnit.gPerM) == 1000
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=gaseousFuelEfficiencyUnit]
     * @returns {number}
     */
    function convertGaseousFuelEfficiencyUnitFrom(value, unit = gaseousFuelEfficiencyUnit) {
        return unit.convert(GaseousFuelEfficiencyUnit.gPerM, value)
    }

    /**
     * Convert from g/m to kg/100km.
     *
     * @memberof! Intl#
     * @param {number|string} value - fuel efficiency value given in g/m unit
     * @param {Object} unit=GaseousFuelEfficiencyUnit.kgPer100km] - the unit to convert to (initially kg/100km)
     * @returns {number}
     */
    function convertGaseousFuelEfficiencyUnitTo(value, unit = GaseousFuelEfficiencyUnit.kgPer100km) {
        return value !== 0 ? GaseousFuelEfficiencyUnit.gPerM.convert(unit, value) : 0
    }

    /**
     * Convert to source currentFuelEfficiencyUnit (mlPerM) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertFuelEfficiencyUnitFrom(1000, FuelEfficiencyUnit.mpgUs) == 0.000425 // mpgUs -> mlPerM
     * intlUS.convertFuelEfficiencyUnitFrom(1000, FuelEfficiencyUnit.lPer100km) == 10 // lPer100km -> mlPerM
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=currentFuelEfficiencyUnit]
     * @returns {number}
     */
    function convertFuelEfficiencyUnitFrom(value, unit = currentFuelEfficiencyUnit) {
        return unit.convert(FuelEfficiencyUnit.mlPerM, value)
    }

    /**
     * Convert from source currentEnergyEfficiencyUnit (kWh/100km) to given intl target or custom unit.
     *
     * @example
     * intlDe.convertEnergyEfficiencyUnitTo(1, EnergyEfficiencyUnit.kWhPer100mi) == 1.609344 // kWhPer100km -> kWhPer100mi
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=currentEnergyEfficiencyUnit]
     * @returns {number}
     */
    function convertEnergyEfficiencyUnitTo(value, unit = currentEnergyEfficiencyUnit) {
        return value !== 0 ? EnergyEfficiencyUnit.kWhPer100km.convert(unit, value) : 0
    }

    /**
     * Convert to source currentEnergyEfficiencyUnit (kWh/100km) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertEnergyEfficiencyUnitFrom(1.609344, EnergyEfficiencyUnit.kWhPer100mi) == 1 // kWhPer100mi -> kWhPer100km
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=currentEnergyEfficiencyUnit]
     * @returns {number}
     */
    function convertEnergyEfficiencyUnitFrom(value, unit = currentEnergyEfficiencyUnit) {
        return unit.convert(EnergyEfficiencyUnit.kWhPer100km, value)
    }

    /**
     * Convert from source energyUnit (kWh) to given intl target or custom unit.
     *
     * @example
     * intlDe.convertEnergyUnitTo(1000, EnergyUnit.wh) == 1000000 // kWh -> wh
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=energyUnit]
     * @returns {number}
     */
    function convertEnergyUnitTo(value, unit = energyUnit) {
        return value !== 0 ? EnergyUnit.kWh.convert(unit, value) : 0
    }

    /**
     * Convert to source energyUnit (kWh) from given intl target or custom unit.
     *
     * @example
     * intlDE.convertEnergyUnitFrom(1000, EnergyUnit.wh) == 1 // wh -> kWh
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=energyUnit]
     * @returns {number}
     */
    function convertEnergyUnitFrom(value, unit = energyUnit) {
        return unit.convert(EnergyUnit.kWh, value)
    }

    /**
     * Convenience function for short position descriptions
     *
     * @memberof! Intl#
     * @param {number} descType - A number, which denotes a reverse gecoding result type
     * @param {Object} params - Position description params as return in the reverse geocoding result
     * @returns {string} a human readable position description
     */
    function formatPositionShort(descType, params) {
        return formatPosition(descType, '_short', params)
    }

    /**
     * Convenience function for detailed position descriptions
     *
     * @memberof! Intl#
     * @param {number} descType - A number, which denotes a reverse gecoding result type
     * @param {Object} params - Position description params as return in the reverse geocoding result
     * @returns {string} a human readable position description
     */
    function formatPositionFull(descType, params) {
        return formatPosition(descType, '', params)
    }

    /**
     * Returns the current locale
     *
     * @memberof! Intl#
     * @returns {string} the current locale
     */
    function getLocale() {
        return locale
    }

    /**
     * Returns true if the current currentFuelEfficiencyUnit is measured per distance e.g. l/100km.
     * Returns false for fuel efficiency based units like miles per gallon.
     *
     * @returns {boolean}
     */
    function isFuelEfficiencyUnitDistanceBased() {
        return currentFuelEfficiencyUnit.exponent !== -1
    }

    /**
     * Returns true if the current currentEnergyEfficiencyUnit is measured per distance e.g. kWh/100km.
     * Returns false for energy efficiency based units like 'miles per kWh' or 'km per kWh'.
     *
     * @returns {boolean}
     */
    function isEnergyEfficiencyUnitDistanceBased() {
        return currentEnergyEfficiencyUnit.exponent !== -1
    }

    /**
     * Convert from source temperatureUnit (°C) to given intl target or custom unit.
     *
     * @example
     * intlUs.convertTemperatureUnitTo(1) == 33.8 // °C -> °F
     * intlUs.convertTemperatureUnitTo(1, TemperatureUnit.kelvin) == 274.15 // °C -> K
     * intlDe.convertTemperatureUnitTo(1) == 1 // °C -> °C
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=temperatureUnit]
     * @returns {number}
     */
    function convertTemperatureUnitTo(value, unit = currentTemperatureUnit) {
        return TemperatureUnit.Celsius.convert(unit, value)
    }

    /**
     * Convert to source temperatureUnit (°C) from given intl target or custom unit.
     *
     * @example
     * intlUs.convertTemperatureUnitFrom(68) == 20 // °F -> °C
     * intlUs.convertTemperatureUnitFrom(68, TemperatureUnit.Fahrenheit) == 20 // °F -> °C
     * intlDe.convertTemperatureUnitFrom(20) == 20 // °C -> °C
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=temperatureUnit]
     * @returns {number}
     */
    function convertTemperatureUnitFrom(value, unit = currentTemperatureUnit) {
        return unit.convert(TemperatureUnit.Celsius, value)
    }

    /**
     * Formats a temperature value given in °C by converting the value to the current target unit (e.g. °F, K)
     *
     * @memberof! Intl#
     * @param {number} temp - Temperature value [°C]
     * @param {string} [formatKey='integer']
     * @param {object} [temperatureUnit=TemperatureUnit.Celsius]
     * @returns {string} the formatted temperature value
     */
    function formatTemperature(temp, formatKey = 'integer', temperatureUnit = currentTemperatureUnit) {
        const temperature = formatTemperatureObject(temp, formatKey, temperatureUnit)
        return `${temperature.value} ${temperature.unit}`
    }

    /**
     * Formats a temperature value given in °C by converting the value to the current target unit (e.g. °F, K)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} temp - Temperature value [°C]
     * @param {string} [formatKey='integer']
     * @param {object} [temperatureUnit]
     * @returns {Object} temperature object
     */
    function formatTemperatureObject(temp, formatKey = 'integer', temperatureUnit = currentTemperatureUnit) {
        return {
            value: formatNumber(convertTemperatureUnitTo(temp, temperatureUnit), formatKey),
            unit: translate(temperatureUnit.translation, null)
        }
    }

    /**
     * Convert from source pressureUnit to given intl target or custom unit.
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=pressureUnit]
     * @returns {number}
     */
    function convertPressureUnitTo(value, unit = currentPressureUnit) {
        return PressureUnit.bar.convert(unit, value)
    }

    /**
     * Convert to source pressureUnit from given intl target or custom unit.
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=pressureUnit]
     * @returns {number}
     */
    function convertPressureUnitFrom(value, unit = currentPressureUnit) {
        return unit.convert(PressureUnit.bar, value)
    }

    /**
     * Formats a pressure value given in bar by converting the value to the current target unit
     *
     * @memberof! Intl#
     * @param {number} pressure - Pressure value in bar
     * @param {string} [formatKey='integer']
     * @returns {string} the formatted pressure value
     */
    function formatPressure(pressure, formatKey = 'bigUnit') {
        const pressureObject = formatPressureObject(pressure, formatKey)
        return `${pressureObject.value} ${pressureObject.unit}`
    }

    /**
     * Formats a pressure value given in bar by converting the value to the current target unit
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} pressure - Pressure value in bar
     * @param {string} [formatKey='integer']
     * @returns {Object} pressure object
     */
    function formatPressureObject(pressure, formatKey = 'bigUnit') {
        return {
            value: formatNumber(convertPressureUnitTo(pressure), formatKey),
            unit: translate(currentPressureUnit.translation, null)
        }
    }

    /**
     * Convert from source powerUnit (kW) to given intl target or custom unit.
     *
     * @example
     * intl.convertPowerUnitTo(1000, PowerUnit.W) == 1000000 // kW -> W
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=powerUnit]
     * @returns {number}
     */
    function convertPowerUnitTo(value, unit = powerUnit) {
        return value !== 0 ? PowerUnit.kW.convert(unit, value) : 0
    }

    /**
     * Convert to source powerUnit (kW) from given intl target or custom unit.
     *
     * @example
     * intl.convertPowerUnitFrom(1000, PowerUnit.W) == 1 // W -> kW
     *
     * @memberof! Intl#
     * @param {number|string} value
     * @param {Object} [unit=powerUnit]
     * @returns {number}
     */
    function convertPowerUnitFrom(value, unit = powerUnit) {
        return unit.convert(PowerUnit.kW, value)
    }

    /**
     * Formats a power unit value given in kW by converting the value to the current target unit (e.g. GW)
     *
     * @memberof! Intl#
     * @param {number} kW
     * @param {string} [formatKey='integerOrBigUnit']
     * @returns {string} the formatted power unit value
     */
    function formatPower(kW, formatKey = 'integerOrBigUnit') {
        const formattedPowerUnitObject = formatPowerObject(kW, formatKey)
        return `${formattedPowerUnitObject.value} ${formattedPowerUnitObject.unit}`
    }

    /**
     * Formats a power unit value given in kW by converting the value to the current target unit (e.g. kW)
     * Returns JSON object with {value, unit}
     *
     * @memberof! Intl#
     * @param {number} power
     * @param {string} [formatKey='integerOrBigUnit']
     * @returns {formattedValue} the formatted power efficiency object
     */
    function formatPowerObject(power, formatKey = 'integerOrBigUnit') {
        return {
            value: formatNumber(power, formatKey),
            unit: translate(powerUnit.translation)
        }
    }

    /**
     * Remove thousand separators and replace decimal separators with the one javascript floating point
     * numbers require
     *
     * @example
     * parseFormattedNumber(intlEn, '1,234.56') === '1234.56'
     * parseFormattedNumber(intlDe, '1.234,56') === '1234.56'
     *
     * @param {string} value - The value to be converted
     * @returns {string} The converted value as string
     */
    function parseFormattedNumber(value) {
        const { decimal, thousands } = numberSeparators

        let result = value

        if (thousands) {
            result = result.replace(new RegExp(`\\${thousands}`, 'g'), '')
        }
        if (decimal) {
            result = result.replace(decimal, '.')
        }

        return result
    }

    return {
        convertDistUnitFrom,
        convertDistUnitTo,
        convertEnergyEfficiencyUnitFrom,
        convertEnergyEfficiencyUnitTo,
        convertEnergyUnitFrom,
        convertEnergyUnitTo,
        convertFuelEfficiencyUnitFrom,
        convertFuelEfficiencyUnitTo,
        convertGaseousFuelEfficiencyUnitFrom,
        convertGaseousFuelEfficiencyUnitTo,
        convertLiquidUnitFrom,
        convertLiquidUnitTo,
        convertPowerUnitFrom,
        convertPowerUnitTo,
        convertPressureUnitFrom,
        convertPressureUnitTo,
        convertSpeedUnitFrom,
        convertSpeedUnitTo,
        convertTemperatureUnitFrom,
        convertTemperatureUnitTo,
        convertWeightUnitFrom,
        convertWeightUnitTo,
        formatAddress,
        formatCoordinates,
        formatCurrency,
        formatDate,
        formatDateOnly,
        formatDateOnlyUTC,
        formatDuration,
        formatDurationMs,
        formatEnergyEfficiency,
        formatEnergyEfficiencyObject,
        formatEnergyUnit,
        formatEnergyUnitObject,
        formatFuelEfficiency,
        formatFuelEfficiencyObject,
        formatFullTimeOnly,
        formatFullTimeOnlyUTC,
        formatGaseousFuelEfficiency,
        formatGaseousFuelEfficiencyObject,
        formatGrams,
        formatGramsObject,
        formatIsoDuration,
        formatKg,
        formatKgObject,
        formatKmh,
        formatKmhObject,
        formatLiters,
        formatLitersObject,
        formatMessage,
        formatMeters,
        formatMetersObject,
        formatMilliliters,
        formatMillilitersObject,
        formatNumber,
        formatPercent,
        formatPositionFull,
        formatPositionShort,
        formatPower,
        formatPowerObject,
        formatPressure,
        formatPressureObject,
        formatRpm,
        formatTemperature,
        formatTemperatureObject,
        formatTimeOnly,
        formatTimeOnlyUTC,
        getDistUnitObject,
        getEnergyEfficiencyUnitObject,
        getEnergyUnitObject,
        getFuelEfficiencyUnitObject,
        getGaseousFuelEfficiencyUnitObject,
        getLocale,
        isEnergyEfficiencyUnitDistanceBased,
        isFuelEfficiencyUnitDistanceBased,
        parseFormattedNumber,
        replaceStringParameters,
        translate
    }

    /**
     * Returns actual unit instances for the given distUnit in settings, which will be used as target units, to which
     * our defined source units will be converted
     *
     * @returns {object}
     */
    function createUnits() {
        let dus, dub
        if (distUnit === 'km') {
            dus = DistUnit.m
            dub = DistUnit.km
        } else {
            dus = DistUnit.yd
            dub = DistUnit.mi
        }
        const distUnitBig = dub
        const distUnitSmall = dus
        const speedUnit = distUnitBig === DistUnit.km ? SpeedUnit.kmh : SpeedUnit.mph
        const weightUnit = distUnitBig === DistUnit.km ? WeightUnit.kg : WeightUnit.lbs
        const rpmUnit = RpmUnit.rpm
        const currentPressureUnit = PressureUnit[pressureUnit]
        const currentTemperatureUnit = TemperatureUnit[temperatureUnit]
        const energyUnit = EnergyUnit.kWh
        const powerUnit = PowerUnit.kW

        let currentFuelEfficiencyUnit
        const fuelEfficiencyUnitStr =
            typeof fuelEfficiencyUnit !== 'string' ? 'l/100km' : fuelEfficiencyUnit.toLowerCase()

        switch (fuelEfficiencyUnitStr) {
            case 'km/l':
                currentFuelEfficiencyUnit = FuelEfficiencyUnit.kmPerL
                break
            case 'ml/m':
                currentFuelEfficiencyUnit = FuelEfficiencyUnit.mlPerM
                break
            case 'mpguk':
                currentFuelEfficiencyUnit = FuelEfficiencyUnit.mpgUk
                break
            case 'mpgus':
                currentFuelEfficiencyUnit = FuelEfficiencyUnit.mpgUs
                break
            case 'km/gal':
                currentFuelEfficiencyUnit = FuelEfficiencyUnit.kpgUs
                break
            default:
                currentFuelEfficiencyUnit = FuelEfficiencyUnit.lPer100km
                break
        }
        let lu
        // RED-2528: no matter which locale, if FuelEfficiencyUnit is kpgUs, the liquid unit will be galUs
        if (currentFuelEfficiencyUnit === FuelEfficiencyUnit.kpgUs) {
            lu = LiquidUnit.galUs
        } else if (distUnitBig === DistUnit.km) {
            lu = LiquidUnit.l
        } else {
            if (locale === 'en-US') {
                lu = LiquidUnit.galUs
            } else {
                lu = LiquidUnit.galImp
            }
        }
        const liquidUnit = lu

        const gaseousFuelEfficiencyUnit = GaseousFuelEfficiencyUnit.kgPer100km

        let currentEnergyEfficiencyUnit
        const energyEfficiencyUnitStr =
            typeof energyEfficiencyUnit !== 'string' ? 'kwh/100km' : energyEfficiencyUnit.toLowerCase()

        switch (energyEfficiencyUnitStr) {
            case 'km/kwh':
                currentEnergyEfficiencyUnit = EnergyEfficiencyUnit.kmPerKWh
                break
            case 'mi/kwh':
                currentEnergyEfficiencyUnit = EnergyEfficiencyUnit.miPerKWh
                break
            case 'kwh/100km':
                currentEnergyEfficiencyUnit = EnergyEfficiencyUnit.kWhPer100km
                break
            default:
                currentEnergyEfficiencyUnit = EnergyEfficiencyUnit.kWhPer100mi
                break
        }

        return {
            distUnitBig,
            distUnitSmall,
            gaseousFuelEfficiencyUnit,
            speedUnit,
            weightUnit,
            liquidUnit,
            currentFuelEfficiencyUnit,
            currentEnergyEfficiencyUnit,
            energyUnit,
            rpmUnit,
            currentPressureUnit,
            currentTemperatureUnit,
            powerUnit
        }
    }

    /**
     * Creates and returns date / time formatters for the predefined format options, that will be cached for
     * object lifetime, to avoid to create one for every formatXXX call
     *
     * @returns {object}
     */
    function createDateFormatters() {
        const dateFormatters = {}
        Object.keys(dateFormatOptions).forEach(formatKey => {
            dateFormatters[formatKey] = new Intl.DateTimeFormat(locale, dateFormatOptions[formatKey])
        })
        return dateFormatters
    }

    /**
     * Creates and returns number formatters for the predefined format options, that will be cached for
     * object lifetime, to avoid to create one for every formatXXX call
     *
     * @returns {object}
     */
    function createNumberFormatters() {
        const numberFormatters = {}
        Object.keys(numberFormatOptions).forEach(formatKey => {
            numberFormatters[formatKey] = new Intl.NumberFormat(locale, numberFormatOptions[formatKey])
        })
        return numberFormatters
    }

    /**
     * Returns a helper object, which is used by replaceStringParameters(...) to format replacement parameters
     * according to to the type and format specs in the appropriate template.
     *
     * @returns {object}
     */
    function createStringifiers() {
        /**
         * Parses a dateTimeString in the format of '22.02.2012 22:45:00' or ISO8601 format '2018-02-23T22:59:59.000Z'
         * and returns a new Date object. Returns null if the given dateTimeString has an invalid format.
         *
         * @param {string} dateTimeString - A date/time string of ISO8601 or "dd.mm.yyyy hh:mm:ss" format.
         * @returns {Date|null}
         */
        function parseDateTime(dateTimeString) {
            if (dateTimeString == null) {
                return null
            }
            if (DATE_REGEX.test(dateTimeString)) {
                const [date, time] = dateTimeString.split(' ')
                const [day, month, year] = date.split('.')
                const [hour, minute, second] = time.split(':')
                // month param index starts from 0
                return new Date(Date.UTC(year, parseInt(month, 10) - 1, day, hour, minute, second))
            }
            if (dayjs(dateTimeString).isValid()) {
                if (!isNaN(Number(dateTimeString))) {
                    return new Date(Number(dateTimeString).valueOf())
                }
                return new Date(Date.parse(dateTimeString))
            }
            return null
        }

        /**
         * Formats a day list using the provided number array in dayList parameter
         * Example [1,3,5] will be formatted to Mo,Mi,Fr
         *
         * @returns {string}
         */
        function formatDayList(dayList, format) {
            let result = ''
            const dayStringCodes = format === 'long' ? DAY_STRINGS : DAY_STRINGS_SHORT
            if (dayList.length < 7) {
                result = dayList
                    .map(day => {
                        return translate(dayStringCodes[parseInt(day, 10) - 1])
                    })
                    .join()
            }
            return result
        }

        return {
            stringcode: (paramName, paramFormat, params) => {
                const stringCode = params[paramName]
                return (stringCode && translate(stringCode, params)) || ''
            },

            time: (paramName, paramFormat, params) => {
                const dateTimeString = params[paramName]
                // if date time input matches with format as
                const dateTime = parseDateTime(dateTimeString)
                return dateTime ? formatTimeOnly(dateTime) : ''
            },

            date: (paramName, paramFormat, params) => {
                const dateTimeString = params[paramName]
                // if date time input matches with format as
                const dateTime = parseDateTime(dateTimeString)
                return dateTime ? formatDateOnly(dateTime) : ''
            },

            interval: (paramName, paramFormat, params) => {
                const start = params['start_' + paramName]
                const end = params['end_' + paramName]

                let result = ''

                if (start && end) {
                    const startDate = parseDateTime(start)
                    const endDate = parseDateTime(end)

                    result = (startDate && endDate && formatDate(startDate) + ' - ' + formatDate(endDate)) || ''
                }
                return result
            },

            daylist: (paramName, paramFormat, params) => {
                let result = ''
                const dayString = params[paramName]
                if (dayString) {
                    result = formatDayList(dayString, paramFormat)
                }
                return result
            },

            duration: (paramName, paramFormat, params) => {
                return formatDuration(params[paramName])
            },

            number: (paramName, paramFormat, params) => {
                const numberString = params[paramName]
                switch (paramFormat) {
                    case 'percent': {
                        const number = parseFloat(numberString) * 100
                        return formatPercent(number)
                    }
                    case 'rpm': {
                        const number = parseInt(numberString, 10)
                        return formatRpm(number)
                    }
                    case 'integer': {
                        const number = parseInt(numberString, 10)
                        return formatNumber(number)
                    }
                    case 'default':
                    default: {
                        const number = parseFloat(numberString)
                        return formatNumber(number)
                    }
                }
            },

            speed: (paramName, paramFormat, params) => {
                const speed = parseFloat(params[paramName])
                return formatKmh(speed)
            },

            distance: (paramName, paramFormat, params) => {
                const dist = parseInt(params[paramName], 10)
                return formatMeters(dist)
            },

            direction: (paramName, paramFormat, params) => {
                const directionCode = params[paramName]
                return (directionCode && translate(`wf_direction_${directionCode}`)) || ''
            },

            difference: (paramName, paramFormat, params) => {
                let result = '---'
                const absoluteDifference = parseFloat(params['absolutefuelleveldifference'])

                // use absolute fuel difference if it is provided
                if (absoluteDifference) {
                    result = formatMilliliters(absoluteDifference)
                } else {
                    const fuelDiff = parseFloat(params[paramName])
                    const tankSize = parseFloat(params['tankSize'])

                    // Only show absolute value if the tanksize is set and bigger than 1 litre, otherwise in percent
                    if (fuelDiff && tankSize && tankSize >= 1000) {
                        const fuelDiffMilliliter = (tankSize / 1000) * fuelDiff
                        result = formatMilliliters(fuelDiffMilliliter)
                    } else if (fuelDiff) {
                        result = formatPercent(fuelDiff / 10)
                    }
                }
                return result
            }
        }
    }

    function getFormattedNumberSeparators() {
        // Check if we already computed the separators for the given locale to return a cached version
        if (!locale) {
            return defaultNumberSeparators
        }

        // Format a dummy value to get the separators and add the result to cache
        const formattedNumber = (10234.56).toLocaleString(locale)
        const match = numberFormatPattern.exec(formattedNumber)

        if (match === null) {
            return defaultNumberSeparators
        }

        return {
            decimal: match[2],
            thousands: match[1]
        }
    }
}

/**
 * @typedef {object} Dms
 * @property {number} deg
 * @property {number} min
 * @property {number} sec
 */

/**
 * Helper function to convert a decimal degree to deg,min,sec structure
 *
 * @private
 * @param {number} dd - value in decimal degree
 * @returns {Dms}
 */
function convert2Dms(dd) {
    dd = Math.abs(dd)
    const deg = Math.trunc(dd)
    const min = Math.trunc((dd % 1) * 60)
    const sec = Math.trunc(((dd * 60) % 1) * 6000) / 100

    return {
        deg,
        min,
        sec
    }
}

/**
 * Returns a DMS (degrees, minutes and seconds) object string representation.
 *
 * @private
 * @param {Dms} dms
 * @param {string} dir
 * @returns {string}
 */
function formatDmsCoord(dms, dir) {
    return dms.deg + '° ' + dms.min + `' ` + dms.sec + '" ' + dir
}
